import { useCallback, useEffect, useState } from "react";
import {
  Vehicle,
  VehicleDTO,
  VehicleType,
  vehicleTypeToName,
} from "types/vehicle.type";
import { RouteUrl } from "routes.type";
import {
  PencilSquareIcon,
  PlusIcon,
  QrCodeIcon,
} from "@heroicons/react/20/solid";
import { TruckIcon } from "@heroicons/react/24/outline";
import { DotMenu } from "components/DotMenu";
import PrimaryButton from "components/buttons/PrimaryButton";
import {
  deleteVehicle,
  useCreateVehicle,
  useUpdateVehicle,
  useVehicles,
  VehicleQueryKey,
} from "api/vehicle.api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { PageLoader } from "components/PageLoader";
import Modal from "components/Modal";
import DangerModal from "components/DangerModal";
import { VehicleModal } from "./VehicleModal";
import { useNotification } from "hooks/useNotification";
import { Card, CardBody, CardHeader, EmptyState } from "./ui";

/**
 * Swedish-plate inspired chip for registration numbers — the one deliberate
 * subject-rooted flourish on this page.
 */
const PlateChip = ({ value }: { value: string }) => (
  <span className="inline-flex items-stretch overflow-hidden rounded-md border border-slate-300 bg-white text-sm font-semibold tracking-widest text-slate-900 shadow-sm">
    <span className="flex w-5 flex-col items-center justify-center bg-blue-700 py-1 text-[8px] font-bold leading-none text-white">
      <span aria-hidden="true">★</span>
      <span className="mt-0.5">S</span>
    </span>
    <span className="px-2.5 py-1 font-mono uppercase">{value}</span>
  </span>
);

export const VehicleTab = () => {
  const notification = useNotification();

  // Vehicles
  const {
    data: vehicles,
    isLoading: isLoadingVehicles,
    isError: isVehiclesError,
    error: vehiclesError,
  } = useVehicles();

  // New vehicle
  const {
    mutate: addVehicle,
    isSuccess: isAddVehicleSuccess,
    isPending: isAddVehicleLoading,
  } = useCreateVehicle();
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);

  const {
    register: registerNewVehicle,
    handleSubmit: handleNewVehicleSubmit,
    reset: resetNewVehicle,
    formState: { errors: newVehicleErrors },
  } = useForm<VehicleDTO>({
    defaultValues: { registrationNumber: "", type: VehicleType.Truck },
  });

  const onNewVehicleSubmit = handleNewVehicleSubmit((data: VehicleDTO) =>
    addVehicle(data),
  );

  const onNewVehicleModalClose = useCallback(() => {
    setIsAddVehicleModalOpen(false);
    setTimeout(() => resetNewVehicle(), 300); // Wait for modal to close
  }, [resetNewVehicle]);

  useEffect(() => {
    document.title = "Fordon";
  }, []);

  useEffect(() => {
    if (isAddVehicleSuccess) {
      onNewVehicleModalClose();
    }
  }, [isAddVehicleSuccess, onNewVehicleModalClose]);

  // update vehicle
  const [vehicleIdToUpdate, setVehicleIdToUpdate] = useState<
    number | undefined
  >();

  const { mutate: updateVehicle, isSuccess: isUpdateVehicleSuccess } =
    useUpdateVehicle(vehicleIdToUpdate);

  const {
    register: registerUpdateVehicle,
    handleSubmit: handleUpdateVehicleSubmit,
    reset: resetUpdateVehicle,
    formState: { errors: updateVehicleErrors },
  } = useForm<VehicleDTO>();

  const onUpdateVehicleSubmit = handleUpdateVehicleSubmit(
    (data: VehicleDTO) => {
      if (!vehicleIdToUpdate) {
        return;
      }
      updateVehicle({ ...data });
    },
  );

  const onUpdateVehicleModalClose = useCallback(() => {
    setVehicleIdToUpdate(undefined);
    setTimeout(() => resetUpdateVehicle(), 300); // Wait for modal to close
  }, [resetUpdateVehicle]);

  useEffect(() => {
    if (vehicleIdToUpdate) {
      const vehicle = vehicles?.find((v) => v.id === vehicleIdToUpdate);
      if (!vehicle) {
        setVehicleIdToUpdate(undefined);
        return;
      }
      resetUpdateVehicle({ ...vehicle });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleIdToUpdate]);

  useEffect(() => {
    if (isUpdateVehicleSuccess) {
      onUpdateVehicleModalClose();
    }
  }, [isUpdateVehicleSuccess, onUpdateVehicleModalClose]);

  // delete vehicle
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | undefined>();
  const queryClient = useQueryClient();
  const { mutate: removeVehicle, isPending: isDeleteVehicleLoading } =
    useMutation({
      ...deleteVehicle(),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [VehicleQueryKey.GetAll],
        });
        notification.info("Fordon borttaget");
        setVehicleToDelete(undefined);
      },
      onError: () => {
        notification.error("Något gick fel, kontakta support");
      },
    });

  const confirmDeleteVehicle = () => {
    if (!vehicleToDelete) {
      return;
    }
    removeVehicle(vehicleToDelete.id);
  };

  const navigateToQrPage = (id: number) =>
    window.open(
      `${window.location.origin}/${RouteUrl.VehicleContentQr}/${id}`,
      "_blank",
    );

  useEffect(() => {
    if (isVehiclesError) {
      const errorMessage =
        vehiclesError instanceof Error
          ? vehiclesError.message
          : "Kunde inte ladda fordon";
      notification.error(errorMessage);
    }
  }, [isVehiclesError, vehiclesError, notification]);

  if (isLoadingVehicles) {
    return <PageLoader />;
  }

  if (isVehiclesError) {
    return (
      <Card>
        <CardHeader
          icon={TruckIcon}
          title="Fordon"
          description="Hantera era fordon och deras QR-koder."
        />
        <CardBody>
          <div className="py-8 text-center text-slate-500">
            <p className="mb-2 font-medium text-slate-700">
              Kunde inte ladda fordon
            </p>
            <p className="text-sm">
              {vehiclesError instanceof Error
                ? vehiclesError.message
                : "Något gick fel"}
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const vehicleList = vehicles ?? [];

  return (
    <Card>
      <Modal
        isOpen={isAddVehicleModalOpen}
        onClose={onNewVehicleModalClose}
        title="Nytt fordon"
        actionText="Spara"
        actionHandler={onNewVehicleSubmit}
        isLoading={isAddVehicleLoading}
      >
        <VehicleModal register={registerNewVehicle} errors={newVehicleErrors} />
      </Modal>
      <Modal
        isOpen={!!vehicleIdToUpdate}
        onClose={onUpdateVehicleModalClose}
        title="Ändra fordon"
        actionText="Spara"
        actionHandler={onUpdateVehicleSubmit}
      >
        <VehicleModal
          register={registerUpdateVehicle}
          errors={updateVehicleErrors}
        />
      </Modal>
      <DangerModal
        isOpen={!!vehicleToDelete}
        onClose={() => setVehicleToDelete(undefined)}
        title="Ta bort fordon"
        paragraphs={[
          `Är du säker på att du vill ta bort fordonet ${vehicleToDelete?.registrationNumber}?`,
          "Detta går inte att ångra. Fordonets QR-kod slutar fungera.",
        ]}
        actionText="Ta bort"
        actionHandler={confirmDeleteVehicle}
        isLoading={isDeleteVehicleLoading}
        loadingText="Tar bort..."
      />

      <CardHeader
        icon={TruckIcon}
        title="Fordon"
        description="Fordonen ni använder för att transportera avfall — registreringsnumret hamnar på transportsedeln. Ni kan även skriva ut en QR-kod för varje fordon och sätta upp i fordonet, som kan scannas för att se lastens innehåll."
        accent="green"
        action={
          <PrimaryButton
            onClick={() => setIsAddVehicleModalOpen(true)}
            size="small"
          >
            <PlusIcon className="mr-1.5 h-4 w-4" /> Nytt fordon
          </PrimaryButton>
        }
      />

      {vehicleList.length === 0 ? (
        <CardBody>
          <EmptyState
            icon={TruckIcon}
            title="Inga fordon än"
            description="Lägg till ert första fordon för att skapa QR-koder."
            action={
              <PrimaryButton
                onClick={() => setIsAddVehicleModalOpen(true)}
                size="small"
              >
                <PlusIcon className="mr-1.5 h-4 w-4" /> Nytt fordon
              </PrimaryButton>
            }
          />
        </CardBody>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="py-3 pl-5 pr-3 font-semibold sm:pl-6">
                  Registreringsnummer
                </th>
                <th className="px-3 py-3 font-semibold">Typ</th>
                <th className="py-3 pl-3 pr-5 sm:pr-6" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vehicleList.map(({ registrationNumber, type, id }: Vehicle) => (
                <tr key={id} className="hover:bg-slate-50/70">
                  <td className="py-3 pl-5 pr-3 sm:pl-6">
                    <PlateChip value={registrationNumber} />
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {vehicleTypeToName(type)}
                  </td>
                  <td className="py-3 pl-3 pr-5 sm:pr-6">
                    <div className="flex justify-end">
                      <DotMenu
                        items={[
                          {
                            label: "Ändra",
                            icon: (
                              <PencilSquareIcon className="h-5 w-5 text-slate-400 group-hover:text-slate-500" />
                            ),
                            onClick: () => setVehicleIdToUpdate(id),
                          },
                          {
                            label: "QR-kod",
                            icon: (
                              <QrCodeIcon className="h-5 w-5 text-slate-400 group-hover:text-slate-500" />
                            ),
                            onClick: () => navigateToQrPage(id),
                          },
                          {
                            label: "Ta bort",
                            isDelete: true,
                            onClick: () =>
                              setVehicleToDelete(
                                vehicleList.find((v) => v.id === id),
                              ),
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
